import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  Trash2, 
  Download, 
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  Settings,
  Users,
  Lock,
  Key
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import RoleService from '../../services/roleService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    status: 'active'
  });

  // Duplicate form state
  const [duplicateName, setDuplicateName] = useState('');

  // Load roles on component mount
  useEffect(() => {
    //console.log('=== RoleManagement useEffect START ===');
    //console.log('Component mounted, loading roles...');
    //console.log('Current roles state:', roles);
    //console.log('Current loading state:', loading);
    loadRoles();
    loadAvailablePermissions();
    //console.log('=== RoleManagement useEffect END ===');
  }, []);

  // Load roles with current filters
  const loadRoles = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.itemsPerPage,
        search: searchTerm,
        status: statusFilter,
        sortBy: 'name',
        sortOrder: 'asc'
      };

      //console.log('=== RoleManagement loadRoles START ===');
      //console.log('Loading roles with params:', params);
      //console.log('Current roles state before API call:', roles);
      
      const response = await RoleService.getRoles(params);
      
      //console.log('RoleService response:', response);
      //console.log('Response data:', response.data);
      //console.log('Response pagination:', response.pagination);
      //console.log('Response data type:', typeof response.data);
      //console.log('Response data length:', Array.isArray(response.data) ? response.data.length : 'Not an array');
      
      const newRoles = response.data || [];
      const newPagination = response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };
      
      //console.log('Setting roles to:', newRoles);
      //console.log('Setting pagination to:', newPagination);
      
      setRoles(newRoles);
      setPagination(newPagination);
      
      //console.log('=== RoleManagement loadRoles END ===');
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load available permissions
  const loadAvailablePermissions = async () => {
    try {
      const response = await RoleService.getAvailablePermissions();
      setAvailablePermissions(response.data);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    loadRoles(1);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') {
      setStatusFilter(value);
    }
    loadRoles(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    loadRoles(1);
  };

  // Handle create role
  const handleCreateRole = async () => {
    try {
      await RoleService.createRole(formData);
      setShowCreateModal(false);
      resetForm();
      loadRoles();
    } catch (error) {
      console.error('Error creating role:', error);
      alert(error.message);
    }
  };

  // Handle edit role
  const handleEditRole = async () => {
    try {
      await RoleService.updateRole(selectedRole.id, formData);
      setShowEditModal(false);
      resetForm();
      setSelectedRole(null);
      loadRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.message);
    }
  };

  // Handle delete role
  const handleDeleteRole = async () => {
    try {
      await RoleService.deleteRole(selectedRole.id);
      setShowDeleteModal(false);
      setSelectedRole(null);
      loadRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      alert(error.message);
    }
  };

  // Handle duplicate role
  const handleDuplicateRole = async () => {
    try {
      await RoleService.duplicateRole(selectedRole.id, duplicateName);
      setShowDuplicateModal(false);
      setSelectedRole(null);
      setDuplicateName('');
      loadRoles();
    } catch (error) {
      console.error('Error duplicating role:', error);
      alert(error.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
      status: 'active'
    });
  };

  // Open edit modal
  const openEditModal = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: [...role.permissions],
      status: role.status
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  // Open duplicate modal
  const openDuplicateModal = (role) => {
    setSelectedRole(role);
    setDuplicateName(`${role.name} (Copy)`);
    setShowDuplicateModal(true);
  };

  // Handle permission toggle
  const handlePermissionToggle = (permission) => {
    const updatedPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter(p => p !== permission)
      : [...formData.permissions, permission];
    
    setFormData({ ...formData, permissions: updatedPermissions });
  };

  // Handle category permission toggle
  const handleCategoryToggle = (categoryPermissions) => {
    const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));
    
    if (allSelected) {
      // Remove all permissions from this category
      const updatedPermissions = formData.permissions.filter(p => !categoryPermissions.includes(p));
      setFormData({ ...formData, permissions: updatedPermissions });
    } else {
      // Add all permissions from this category
      const newPermissions = [...new Set([...formData.permissions, ...categoryPermissions])];
      setFormData({ ...formData, permissions: newPermissions });
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#10b981', icon: CheckCircle, text: 'Active' },
      inactive: { color: '#6b7280', icon: XCircle, text: 'Inactive' }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: `${config.color}20`,
        color: config.color
      }}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Role Name',
      sortable: true,
      render: (role) => {
        if (!role) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: role.isSystemRole ? '#fef3c7' : 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: role.isSystemRole ? '#d97706' : 'white',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <Shield size={20} />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                {role.name}
                {role.isSystemRole && (
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 6px',
                    backgroundColor: '#fef3c7',
                    color: '#d97706',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    SYSTEM
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {role.description}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'permissions',
      label: 'Permissions',
      render: (role) => {
        if (!role) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Key size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>
              {role.permissions ? role.permissions.length : 0} permissions
            </span>
          </div>
        );
      }
    },
    {
      key: 'userCount',
      label: 'Users',
      sortable: true,
      render: (role) => {
        if (!role) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>{role.userCount || 0}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (role) => {
        if (!role) return null;
        return getStatusBadge(role.status);
      }
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (role) => {
        if (!role || !role.createdAt) return null;
        
        return (
          <span style={{ fontSize: '14px' }}>
            {new Date(role.createdAt).toLocaleDateString()}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (role) => {
        if (!role) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => openEditModal(role)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Edit Role"
            >
              <Edit size={16} />
            </button>
            
            <button
              onClick={() => openDuplicateModal(role)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Duplicate Role"
            >
              <Copy size={16} />
            </button>

            {!role.isSystemRole && (role.userCount === 0 || !role.userCount) && (
              <button
                onClick={() => openDeleteModal(role)}
                style={{
                  padding: '6px',
                  backgroundColor: '#fef2f2',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#dc2626'
                }}
                title="Delete Role"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Role Management"
        description="Manage roles, permissions, and access control across the system"
      />
      <div className="page-container">

      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={20} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }} />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                padding: '10px 12px 10px 40px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                width: '300px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={clearFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Filter size={16} />
            Clear Filters
          </button>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => loadRoles()}
            className="btn-outline-cam"
          >
            <RefreshCw size={16} />
            Refresh
          </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-cam"
            >
              <Plus size={16} />
              Add Role
            </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={roles}
        columns={columns}
        loading={loading}
        pagination={true}
        pageSize={pagination.itemsPerPage || 10}
        searchable={false}
        sortable={true}
        exportable={true}
        selectable={false}
      />

      {/* Create Role Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Create New Role
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Role Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                Permissions
              </label>
              <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px' }}>
                {availablePermissions.map((category, index) => (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px'
                    }}>
                      <input
                        type="checkbox"
                        checked={category.permissions.every(p => formData.permissions.includes(p))}
                        onChange={() => handleCategoryToggle(category.permissions)}
                        style={{ margin: 0 }}
                      />
                      <span style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                        {category.category}
                      </span>
                    </div>
                    <div style={{ paddingLeft: '24px' }}>
                      {category.permissions.map((permission, permIndex) => (
                        <div key={permIndex} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission)}
                            onChange={() => handlePermissionToggle(permission)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            {permission}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                className="btn-cam"
              >
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Edit Role
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Role Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={selectedRole?.isSystemRole}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  backgroundColor: selectedRole?.isSystemRole ? '#f9fafb' : 'white'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                Permissions
              </label>
              <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px' }}>
                {availablePermissions.map((category, index) => (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px'
                    }}>
                      <input
                        type="checkbox"
                        checked={category.permissions.every(p => formData.permissions.includes(p))}
                        onChange={() => handleCategoryToggle(category.permissions)}
                        style={{ margin: 0 }}
                      />
                      <span style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                        {category.category}
                      </span>
                    </div>
                    <div style={{ paddingLeft: '24px' }}>
                      {category.permissions.map((permission, permIndex) => (
                        <div key={permIndex} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission)}
                            onChange={() => handlePermissionToggle(permission)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            {permission}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                  setSelectedRole(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditRole}
                className="btn-cam"
              >
                Update Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Role Modal */}
      {showDuplicateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Duplicate Role
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                New Role Name *
              </label>
              <input
                type="text"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setSelectedRole(null);
                  setDuplicateName('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDuplicateRole}
                className="btn-cam"
              >
                Duplicate Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRole(null);
        }}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${selectedRole?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      </div>
    </>
  );
};

export default RoleManagement;
